"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import {
  buildDashboardWidgetSettings,
  type NormalizedWidgetSettingRow,
  normalizeWidgetSettingsPayload,
  roleOrdersEqual,
  widgetRolesForPut,
} from "@/lib/dashboard/widget-setting-model";
import {
  fetchWidgetDataBatched,
  fetchWidgetSettings,
  putWidgetSetting,
  type WidgetPutBody,
} from "@/lib/dashboard/widgets-client";
import { widgetsDebug } from "@/lib/dashboard/widgets-debug-log";
import type {
  DashboardWidgetId,
  DashboardWidgetSettings,
  UserRole,
} from "@/lib/types/dashboard-widgets";
import { QueryKeys } from "@/lib/types/query-keys";

function stableSortedStrings(values: readonly string[]): string[] {
  return [...values].slice().sort();
}

function normalizedRolesEqualRoles(
  a: readonly UserRole[],
  b: readonly UserRole[],
): boolean {
  const ra = stableSortedStrings(a.map((x) => x.toLowerCase()));
  const rb = stableSortedStrings(b.map((x) => x.toLowerCase()));
  return ra.join(",") === rb.join(",");
}

function normalizedPayloadEqual(
  a: NormalizedWidgetSettingRow[],
  b: NormalizedWidgetSettingRow[],
): boolean {
  if (a.length !== b.length) return false;
  const byWidget = new Map(b.map((r) => [r.widgetId, r]));
  for (const row of a) {
    const o = byWidget.get(row.widgetId);
    if (!o) return false;
    if (o.settingId !== row.settingId) return false;
    if (o.category !== row.category) return false;
    if (o.apiOrderIndex !== row.apiOrderIndex) return false;
    if (!roleOrdersEqual(row.orderByRole, o.orderByRole)) return false;
    if (!normalizedRolesEqualRoles(row.rolesForWidget, o.rolesForWidget))
      return false;
  }
  return true;
}

export function useNormalizedWidgetSettingsQuery() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  return useQuery({
    queryKey: [QueryKeys.WidgetSettings, tenantId],
    enabled: !!accessToken && tenantId != null && tenantId > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for widget settings");
      }
      const raw = await fetchWidgetSettings(token, tid);
      const normalized = normalizeWidgetSettingsPayload(raw);
      widgetsDebug("query: widget settings normalized", {
        tenantId: tid,
        rowCount: normalized.length,
        normalized: normalized.map((r) => ({
          settingId: r.settingId,
          key: r.key,
          category: r.category,
          rolesForWidget: r.rolesForWidget,
        })),
      });
      return normalized;
    },
  });
}

export function useTenantDashboardWidgetLayouts() {
  const q = useNormalizedWidgetSettingsQuery();
  const layouts: DashboardWidgetSettings = useMemo(
    () =>
      q.data?.length
        ? buildDashboardWidgetSettings(q.data)
        : buildDashboardWidgetSettings([]),
    [q.data],
  );

  const apiDrivenLayouts = !!(q.data && q.data.length > 0);

  /** True until the first widget-settings fetch completes (avoid default layout flash). */
  const layoutsPending = q.isPending;

  return {
    normalizedRows: q.data ?? [],
    layouts,
    layoutsPending,
    isPending: q.isPending,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
    apiDrivenLayouts,
  };
}

export function useWidgetAggregateQuery(
  kpiWidgets: DashboardWidgetId[],
  options?: { enabled?: boolean },
) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const sortedKeys = useMemo(() => [...kpiWidgets].sort(), [kpiWidgets]);

  return useQuery({
    queryKey: [QueryKeys.WidgetData, tenantId, sortedKeys.join("|")],
    enabled:
      (options?.enabled ?? true) &&
      !!accessToken &&
      tenantId != null &&
      tenantId > 0 &&
      sortedKeys.length > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for widget data");
      }
      return fetchWidgetDataBatched(token, tid, sortedKeys);
    },
    staleTime: 60_000,
  });
}

export function useBulkSaveWidgetSettingsMutation() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (
      pairs: Array<{
        baseline: NormalizedWidgetSettingRow;
        draft: NormalizedWidgetSettingRow;
      }>,
    ) => {
      if (!accessToken || tenantId == null) {
        throw new Error("Missing session or tenant");
      }

      widgetsDebug("mutation: save widget settings - pair count", pairs.length);
      const putDetails: Array<{
        settingId: number;
        key: string;
        baselineRoles: UserRole[];
        draftRoles: UserRole[];
        baselineCategory: string;
        draftCategory: string;
      }> = [];

      /* eslint-disable no-await-in-loop */
      for (const { baseline, draft } of pairs) {
        const catChanged = baseline.category !== draft.category;
        const rolesChanged = !normalizedRolesEqualRoles(
          baseline.rolesForWidget,
          draft.rolesForWidget,
        );
        const orderChanged =
          baseline.apiOrderIndex !== draft.apiOrderIndex ||
          !roleOrdersEqual(baseline.orderByRole, draft.orderByRole);
        if (!(catChanged || rolesChanged || orderChanged)) continue;

        const body: WidgetPutBody = {
          name: draft.name,
          displayName: draft.displayName,
          category: draft.category,
          widgetRoles: widgetRolesForPut(
            draft.rolesForWidget,
            draft.apiTokensByRole,
          ),
          sortOrder: draft.apiOrderIndex,
          roleOrders: draft.rolesForWidget.map((role) => ({
            role: draft.apiTokensByRole[role] || role,
            sortOrder: draft.orderByRole[role] ?? draft.apiOrderIndex,
          })),
        };

        putDetails.push({
          settingId: draft.settingId,
          key: draft.key,
          baselineRoles: [...baseline.rolesForWidget],
          draftRoles: [...draft.rolesForWidget],
          baselineCategory: baseline.category,
          draftCategory: draft.category,
        });

        widgetsDebug("mutation: PUT pending", {
          settingId: draft.settingId,
          key: draft.key,
          body,
        });

        await putWidgetSetting(accessToken, tenantId, draft.settingId, body);
      }
      /* eslint-enable no-await-in-loop */

      widgetsDebug("mutation: save complete", {
        putCount: putDetails.length,
        putDetails,
      });
    },
    onSuccess: async (_data, pairs) => {
      if (pairs.length > 0) {
        toast({
          title: "Dashboard widgets saved",
          description:
            "Widget visibility, order, and KPI layout were updated for this tenant.",
          status: "success",
          duration: 3500,
          isClosable: true,
          position: "top-right",
        });
      }
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WidgetSettings, tenantId],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WidgetData, tenantId],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not save widget settings",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
}

export function normalizedDraftEqualsBaseline(
  draft: NormalizedWidgetSettingRow[],
  baseline: NormalizedWidgetSettingRow[],
): boolean {
  return normalizedPayloadEqual(draft, baseline);
}
